import { applicationApi } from '@/api';
import {
  IAgentGigCompliance,
  IApplicationDetails,
  IApplicationSummary,
  ICommunicationLog,
  ICommunicationStatus,
  ICommunicationType,
  ISaveCommunicationLogPayload,
} from '@/api/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { IApplicationStatusValidation, ICommunicationLogPayload, IThunkAPI } from '@/store/types';

export const initialState: IApplicationDetails = {
  applicationDetails: null,
  isApplicationError: false,
  isApplicationSuccess: false,
  isApplicationFetching: false,
  applicationStatus: {}, // type = { statusId: loadingBoolean }
  communicationLogs: [],
  communicationStatusList: [],
  communicationTypes: [],
  agentGigCompliance: null,
  selectedCampaign: 'current',
  activeTab: 'Profile',
};

export const getApplicationDetailsByApplicationId = createAsyncThunk<
  { id: number; data: IApplicationSummary },
  number,
  IThunkAPI
>('get/application/SU/details', async (id, thunkAPI) => {
  try {
    const response = await applicationApi.getApplicationDetailsByApplicationId(id);
    return { id: id, data: response.data };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getAgentGigCompliance = createAsyncThunk<IAgentGigCompliance, number, IThunkAPI>(
  'su/get/agentGigComplicance',
  async (applicationId, thunkAPI) => {
    try {
      const response = await applicationApi.getAgentGigCompliance(applicationId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCommunicationLogList = createAsyncThunk<ICommunicationLog[], ICommunicationLogPayload, IThunkAPI>(
  'get/communication/log',
  async (payload, thunkAPI) => {
    try {
      const response = await applicationApi.getCommunicationLogList(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCommunicationTypeList = createAsyncThunk<ICommunicationType[], undefined, IThunkAPI>(
  'get/communication/type',
  async (_, thunkAPI) => {
    try {
      const response = await applicationApi.getCommunicationTypeList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCommunicationStatusList = createAsyncThunk<ICommunicationStatus[], number, IThunkAPI>(
  'get/communication/status',
  async (communicationTypeId, thunkAPI) => {
    try {
      const response = await applicationApi.getCommunicationStatusList(communicationTypeId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const saveCommunicationLog = createAsyncThunk<unknown, ISaveCommunicationLogPayload, IThunkAPI>(
  'get/communication/log/save',
  async (payload, thunkAPI) => {
    try {
      const response = await applicationApi.saveCommmunicationLog(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const deleteCommunicationLog = createAsyncThunk<unknown, number, IThunkAPI>(
  'communication/log/delete',
  async (communicationLogId, thunkAPI) => {
    try {
      const response = await applicationApi.deleteCommunicationLog(communicationLogId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getApplicationStatusValidation = createAsyncThunk<any, IApplicationStatusValidation, IThunkAPI>(
  'get/application/status/validation',
  async (application, thunkAPI) => {
    try {
      const response = await applicationApi.getApplicationStatusValidation(
        application.applicationId,
        application.currentApplicationStatusId,
      );
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const applicationSlice = createSlice({
  name: 'applicantDetails',
  initialState,
  reducers: {
    clearapplicationss(state) {
      state.applicationDetails = null;
    },
    setSelectedCampaign(state, action) {
      state.selectedCampaign = action.payload;
    },
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getApplicationDetailsByApplicationId.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.applicationDetails = { ...state.applicationDetails, [action.payload.id]: action?.payload.data };
        }
        state.isApplicationSuccess = true;
        state.isApplicationError = false;
        state.isApplicationFetching = false;
      })
      .addCase(getApplicationDetailsByApplicationId.rejected, state => {
        state.isApplicationError = true;
        state.isApplicationSuccess = false;
        state.isApplicationFetching = false;
      })
      .addCase(getApplicationDetailsByApplicationId.pending, state => {
        state.isApplicationFetching = true;
      })
      .addCase(getCommunicationLogList.fulfilled, (state, action) => {
        state.communicationLogs = action.payload;
      })
      .addCase(getCommunicationTypeList.fulfilled, (state, action) => {
        state.communicationTypes = action.payload;
      })
      .addCase(getCommunicationStatusList.fulfilled, (state, action) => {
        state.communicationStatusList = action.payload;
      })
      .addCase(getAgentGigCompliance.fulfilled, (state, action) => {
        state.agentGigCompliance = action.payload;
      });
  },
});

// actions
export const { clearapplicationss, setSelectedCampaign, setActiveTab } = applicationSlice.actions;

// selectors

export const applicationStatusSelector = (state: RootState): IApplicationDetails['applicationStatus'] =>
  state.applicantDetails.applicationStatus;
export default applicationSlice.reducer;
